import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import styles from "./style.module.scss";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../../state";
import { isEqual } from "lodash";
import { getPreferredFormattedTimeSeconds } from "../../../utils/preferences";
import { FiSettings } from "react-icons/fi";
import { Tooltip } from "react-tooltip";
import {
  getEnergyValue,
  getLapFlagGradient,
  getLapTimeValue,
  getPitInSoCValue,
  getPitWindowOpenCloseTargets,
  getPitWindowStatus,
  getTCellMaxValue,
  getTimeRemainingValues,
} from "../../../lib/race";
import { MutateSetLapComment } from "../../../utils/graphql/hooks/raceDataHooks";
import { setLapCommentLocal } from "../../../state/raceDataReducer";

const HEADERS = [
  { key: "lapNumber", label: "Lap", dp: 0, width: 40 },
  { key: "energy", label: "Energy", dp: 2, width: 80 },
  { key: "target", label: "Target", dp: 2, width: 80 },
  { key: "eLap", label: "eLap", dp: 2, width: 70 },
  { key: "deltaELap", label: "Δ eLap", dp: 2, width: 80 },
  { key: "lapTime", label: "LT", dp: 3, width: 70 },
  { key: "deltaLapTime", label: "Δ LT", dp: 3, width: 75 },
  { key: "tCellPred", label: "TCellPred", dp: 1, width: 90 },
  { key: "pitInSoC", label: "SoC", dp: 1, width: 70 },
  { key: "eLapOpen", label: "Open", dp: 2, width: 70 },
  { key: "eLapClose", label: "Close", dp: 2, width: 70 },
  { key: "timeRemainingRace", label: "tRem", dp: 0, width: 70 },
  { key: "timeRemainingMaximum", label: "tRemMax", dp: 0, width: 90 },
  { key: "comment", label: "Comment", dp: 0, width: 150 },
] as const;

// Load visible column preference from localStorage
const visibleHeaderKeysSave = localStorage.getItem("raceTableVisibleHeaders");
const visibleHeaderKeysDefault = visibleHeaderKeysSave
  ? (JSON.parse(visibleHeaderKeysSave) as string[])
  : [
      "lapNumber",
      "energy",
      "target",
      "eLap",
      "deltaELap",
      "lapTime",
      "deltaLapTime",
      "pitInSoC",
    ];

export default function RaceTable({ carColour }: { carColour: string }) {
  const dispatch = useDispatch();
  const pitBoostTooltipId = useId();

  const lapTableData = useSelector(
    (state: RootState) => state.raceData[carColour]?.lapTable ?? {},
    isEqual,
  );

  const currentLap = useSelector(
    (state: RootState) => state.raceData[carColour]?.raceConfig?.currentLap,
  );

  const raceConfig = useSelector(
    (state: RootState) => state.raceData[carColour]?.raceConfig,
    isEqual,
  );

  const ressPitBoostLap = useSelector(
    (state: RootState) => state.raceData[carColour]?.ressPitBoostLap ?? null,
  );

  const jagDrivers = useSelector(
    (state: RootState) => state.imports.derivedImportData.strategy.jagDrivers,
    isEqual,
  );
  const driverInfo = useSelector(
    (state: RootState) => state.imports.derivedImportData.strategy.driverInfo,
    isEqual,
  );
  const segmentTimeFractions = useSelector(
    (state: RootState) =>
      state.imports.derivedImportData.em?.segmentTimeFractions,
    isEqual,
  );
  const strategyImport = useSelector(
    (state: RootState) => state.imports.imports.strategy?.data,
    isEqual,
  );
  const emImport = useSelector(
    (state: RootState) => state.imports.imports.em?.data,
    isEqual,
  );
  const isPitBoostRace = useSelector(
    (state: RootState) =>
      state.imports.imports.strategy?.data.BPitBoostRace === 1,
    isEqual,
  );

  const encryptionConfig = useSelector(
    (state: RootState) => state.imports.derivedImportData.encryption?.config,
    isEqual,
  );

  const encryptionPriority = useSelector(
    (state: RootState) => state.imports.derivedImportData.encryption?.priority,
    isEqual,
  );

  // Get display name and colour for car from driver info
  const driverNumber = jagDrivers[carColour];
  const driver = driverNumber ? driverInfo[driverNumber] : null;
  const carConfig = {
    name: driver?.driverName || carColour,
    colour: driver?.driverHighlightColourAlt || "#888",
    fontColour: driver?.driverHighlightColourFontColourAlt || "white",
  };

  const flagPeriods = useSelector(
    (state: RootState) => state.interventions.flagPeriods,
    isEqual,
  );

  const lapNumbers = useMemo(() => {
    return Object.keys(lapTableData)
      .map(Number)
      .sort((a, b) => a - b);
  }, [lapTableData]);

  // Headers from the encryption import, for shared channels enforce priority (default to green)
  const channelHeaders = useMemo(() => {
    const headers: {
      key: string;
      label: string;
      dp: number;
      width: number;
      fontColour: string | undefined;
      channelColours?: ("Green" | "Orange")[];
    }[] = [];
    if (encryptionConfig) {
      const colourOrderByChannel = new Map<string, ("Green" | "Orange")[]>();

      (["Green", "Orange"] as const).forEach((colour) => {
        encryptionConfig[colour].channels.forEach((channelId) => {
          if (!channelId || channelId.trim() === "") return;

          const existing = colourOrderByChannel.get(channelId);
          if (!existing) {
            colourOrderByChannel.set(channelId, [colour]);
            return;
          }
          if (existing.includes(colour)) return;

          const existingColour = existing[0];
          const existingPriority =
            encryptionPriority?.[existingColour]?.[channelId] ?? 0;
          const candidatePriority =
            encryptionPriority?.[colour]?.[channelId] ?? 0;

          const preferCandidate =
            candidatePriority > 0 && candidatePriority < existingPriority;

          colourOrderByChannel.set(
            channelId,
            preferCandidate
              ? [colour, existingColour]
              : [existingColour, colour],
          );
        });
      });

      colourOrderByChannel.forEach((channelColours, channelId) => {
        const primaryColour = channelColours[0];
        const index =
          encryptionConfig[primaryColour].channels.indexOf(channelId);
        headers.push({
          key: `ch:${channelId}`,
          label: channelId,
          dp: encryptionConfig[primaryColour].decimals?.[index] ?? 2,
          width: 70,
          fontColour:
            channelColours.length === 1
              ? primaryColour === "Orange"
                ? "#f58231"
                : "#3cb44b"
              : undefined,
          channelColours,
        });
      });
    }
    return headers;
  }, [encryptionConfig, encryptionPriority]);

  const allHeaders = useMemo(() => {
    const mappedHeaders = HEADERS.map((header) => ({
      ...header,
      fontColour: undefined as string | undefined,
      channelColours: undefined as ("Green" | "Orange")[] | undefined,
    }));
    const commentHeaders = mappedHeaders.filter(
      (header) => header.key === "comment",
    );
    const otherHeaders = mappedHeaders.filter(
      (header) => header.key !== "comment",
    );
    return [...otherHeaders, ...channelHeaders, ...commentHeaders];
  }, [channelHeaders]);

  const allHeaderKeys = useMemo(
    () => new Set(allHeaders.map((header) => header.key)),
    [allHeaders],
  );

  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const [visibleHeaderKeys, setVisibleHeaderKeys] = useState<string[]>(
    visibleHeaderKeysDefault,
  );

  // Drop any stored keys that no longer exist
  const effectiveVisibleKeys = useMemo(() => {
    const filtered = visibleHeaderKeys.filter((key) => allHeaderKeys.has(key));
    if (filtered.length > 0) return filtered;
    return visibleHeaderKeysDefault.filter((key) => allHeaderKeys.has(key));
  }, [visibleHeaderKeys, allHeaderKeys]);

  // Resise observer instead of media query for height to allow width to be variable depending on headers
  const containerRef = useRef<HTMLDivElement>(null);
  const [isTallContainer, setIsTallContainer] = useState(false);
  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const observer = new ResizeObserver(([entry]) => {
      setIsTallContainer(entry.contentRect.height >= 1125);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  const visibleHeaders = allHeaders.filter((header) =>
    effectiveVisibleKeys.includes(header.key),
  );

  const toggleHeader = (key: string) => {
    setVisibleHeaderKeys((currentKeys) => {
      let updatedKeys;
      if (effectiveVisibleKeys.includes(key)) {
        if (effectiveVisibleKeys.length === 1) return currentKeys;
        updatedKeys = currentKeys.filter((currentKey) => currentKey !== key);
      } else {
        updatedKeys = [...currentKeys, key];
      }
      localStorage.setItem(
        "raceTableVisibleHeaders",
        JSON.stringify(updatedKeys),
      );
      return updatedKeys;
    });
  };

  const formatValue = (value: number | null | undefined, dp: number) => {
    if (value === null || value === undefined) return "-";
    return value.toFixed(dp);
  };

  const getHeaderTextStyle = (header: {
    fontColour: string | undefined;
    channelColours?: ("Green" | "Orange")[];
  }) => {
    if (header.channelColours && header.channelColours.length === 2) {
      return {
        backgroundImage: "linear-gradient(90deg, #3cb44b 50%, #f58231 50%)",
        WebkitBackgroundClip: "text",
        backgroundClip: "text",
        color: "transparent",
      };
    }
    return { color: header.fontColour };
  };

  const setLapComment = MutateSetLapComment();

  return (
    <div
      ref={containerRef}
      className={`${styles.container} ${
        isTallContainer ? styles.tallContainer : ""
      }`}
    >
      <Tooltip
        id={pitBoostTooltipId}
        place="top"
        positionStrategy="fixed"
        style={{ zIndex: 9999 }}
      />
      <table className={showColumnSettings ? styles.settingsOpen : undefined}>
        <colgroup>
          {visibleHeaders.map((header) => (
            <col key={header.key} style={{ width: `${header.width}px` }} />
          ))}
        </colgroup>
        <thead>
          <tr className={styles.titleRow}>
            <th
              colSpan={visibleHeaders.length}
              style={{
                backgroundColor: carConfig.colour,
                color: carConfig.fontColour,
              }}
            >
              {carConfig.name} - Race Table
              <button
                className={styles.settingsButton}
                type="button"
                onClick={() => setShowColumnSettings((isOpen) => !isOpen)}
              >
                <FiSettings />
              </button>
            </th>
          </tr>
          <tr>
            {visibleHeaders.map((header) => (
              <th key={header.key} style={{ width: `${header.width}px` }}>
                <span style={getHeaderTextStyle(header)}>{header.label}</span>
              </th>
            ))}
          </tr>
          {showColumnSettings && (
            <tr className={styles.settingsRow}>
              <th colSpan={visibleHeaders.length}>
                <div className={styles.settingsList}>
                  {allHeaders.map((header) => (
                    <label key={header.key} className={styles.checkbox}>
                      <input
                        type="checkbox"
                        checked={effectiveVisibleKeys.includes(header.key)}
                        disabled={
                          effectiveVisibleKeys.length === 1 &&
                          effectiveVisibleKeys.includes(header.key)
                        }
                        onChange={() => toggleHeader(header.key)}
                      />
                      <span style={getHeaderTextStyle(header)}>
                        {header.label}
                      </span>
                    </label>
                  ))}
                </div>
              </th>
            </tr>
          )}
        </thead>
        <tbody>
          {lapNumbers.map((lapNum) => {
            const lap = lapTableData[lapNum];
            const pitWindowELap =
              isPitBoostRace && raceConfig
                ? getPitWindowOpenCloseTargets(
                    lapNum,
                    lapTableData,
                    raceConfig,
                    strategyImport?.EConsumptionLastCorner2PitIn ?? 0,
                  )
                : null;
            return (
              <React.Fragment key={lapNum}>
                <tr
                  className={
                    lapNum === currentLap ? styles.currentLapRow : undefined
                  }
                >
                  {visibleHeaders.map((header) => {
                    if (header.key === "lapNumber") {
                      const flagGradient = getLapFlagGradient(
                        lapTableData,
                        lapNum,
                        flagPeriods,
                      );
                      return (
                        <td
                          key={header.key}
                          style={{ backgroundImage: flagGradient }}
                        >
                          {formatValue(lap.lapNumber, header.dp)}
                        </td>
                      );
                    }
                    if (header.key === "energy") {
                      const { value, isReal } = getEnergyValue(
                        lapTableData,
                        lapNum,
                      );
                      return (
                        <td
                          key={header.key}
                          style={{ opacity: isReal ? 1 : 0.3 }}
                        >
                          {formatValue(value, header.dp)}
                        </td>
                      );
                    }

                    if (header.key === "lapTime") {
                      const { value, isReal } = getLapTimeValue(
                        lapTableData,
                        lapNum,
                      );
                      return (
                        <td
                          key={header.key}
                          style={{ opacity: isReal ? 1 : 0.3 }}
                        >
                          {value != null
                            ? getPreferredFormattedTimeSeconds(
                                value,
                                true,
                                header.dp,
                              )
                            : "-"}
                        </td>
                      );
                    }
                    if (
                      header.key === "timeRemainingRace" ||
                      header.key === "timeRemainingMaximum"
                    ) {
                      const { race, max } = getTimeRemainingValues(
                        lapTableData,
                        lapNum,
                        strategyImport?.tRaceMaximum,
                        strategyImport?.tRaceMaximumWithSuspension,
                        flagPeriods,
                      );
                      const value =
                        header.key === "timeRemainingRace" ? race : max;
                      const isReal = lapTableData[lapNum]?.lapTime != null;
                      return (
                        <td
                          key={header.key}
                          style={{ opacity: isReal ? 1 : 0.3 }}
                        >
                          {value != null
                            ? getPreferredFormattedTimeSeconds(
                                value,
                                true,
                                header.dp,
                              )
                            : "-"}
                        </td>
                      );
                    }
                    if (header.key === "pitInSoC") {
                      const { value, isReal } = getPitInSoCValue(
                        lapTableData,
                        lapNum,
                      );
                      const pitLap = raceConfig?.pitLap ?? null;
                      const isBeforeOrAtPitLap =
                        pitLap === null || lapNum <= pitLap;
                      const pitWindowStatus =
                        !isPitBoostRace || !isBeforeOrAtPitLap
                          ? null
                          : getPitWindowStatus(
                              value,
                              strategyImport?.rBMSSoCMinMaxPitWindowPrediction ??
                                0,
                              strategyImport?.rBMSSoCMinMinPitWindowPrediction ??
                                0,
                            );
                      const bgColor =
                        pitWindowStatus === 1
                          ? "#69db7c"
                          : pitWindowStatus === 0
                          ? "#ffa94d"
                          : undefined;
                      return (
                        <td
                          key={header.key}
                          style={{
                            color: isReal ? undefined : "rgba(0, 0, 0, 0.3)",
                            backgroundColor: bgColor,
                          }}
                        >
                          {value == null ? "-" : `${value.toFixed(header.dp)}%`}
                        </td>
                      );
                    }
                    if (
                      header.key === "eLapOpen" ||
                      header.key === "eLapClose"
                    ) {
                      const pitLap = raceConfig?.pitLap ?? null;
                      const isBeforeOrAtPitLap =
                        pitLap === null || lapNum <= pitLap;
                      if (
                        !isPitBoostRace ||
                        !isBeforeOrAtPitLap ||
                        !pitWindowELap
                      ) {
                        return <td key={header.key}>-</td>;
                      }
                      const value =
                        header.key === "eLapOpen"
                          ? pitWindowELap.open
                          : pitWindowELap.close;
                      // TODO: Clamp this to min and max consumption
                      const displayedValue =
                        value !== null && value >= 1 && value <= 3
                          ? value
                          : null;

                      return (
                        <td key={header.key}>
                          {formatValue(displayedValue, header.dp)}
                        </td>
                      );
                    }
                    if (header.key === "deltaLapTime") {
                      const value =
                        lap.lapTime !== null && lap.predictedLapTime !== null
                          ? lap.lapTime - lap.predictedLapTime
                          : null;
                      const bgColor =
                        value === null
                          ? undefined
                          : value > 0.01
                          ? "#ff6b6b"
                          : value < -0.01
                          ? "#69db7c"
                          : undefined;
                      return (
                        <td
                          key={header.key}
                          style={
                            bgColor ? { backgroundColor: bgColor } : undefined
                          }
                        >
                          {value != null
                            ? (value > 0 ? "+" : "") +
                              getPreferredFormattedTimeSeconds(
                                value,
                                true,
                                header.dp,
                              )
                            : "-"}
                        </td>
                      );
                    }
                    if (header.key === "deltaELap") {
                      const value =
                        lap.eLap !== null && lap.target !== null
                          ? lap.eLap - lap.target
                          : null;
                      const bgColor =
                        value === null
                          ? undefined
                          : value > 0.01
                          ? "#ff6b6b"
                          : value < -0.01
                          ? "#69db7c"
                          : undefined;
                      return (
                        <td
                          key={header.key}
                          style={
                            bgColor ? { backgroundColor: bgColor } : undefined
                          }
                        >
                          {formatValue(value, header.dp)}
                        </td>
                      );
                    }
                    if (header.key === "tCellPred") {
                      const { value, isReal } = getTCellMaxValue(
                        lapTableData,
                        lapNumbers,
                        lapNum,
                      );
                      const isPredictedPitBoostLap =
                        isPitBoostRace &&
                        raceConfig?.pitLap === null &&
                        ressPitBoostLap === lapNum;
                      return (
                        <td
                          key={header.key}
                          data-tooltip-id={
                            isPredictedPitBoostLap
                              ? pitBoostTooltipId
                              : undefined
                          }
                          data-tooltip-content={
                            isPredictedPitBoostLap
                              ? "Predicted pit boost lap"
                              : undefined
                          }
                          style={{
                            backgroundColor: isPredictedPitBoostLap
                              ? "#74c0fc"
                              : undefined,
                            color:
                              isPredictedPitBoostLap && !isReal
                                ? "rgba(0, 0, 0, 0.3)"
                                : undefined,
                            opacity: isPredictedPitBoostLap || isReal ? 1 : 0.3,
                          }}
                        >
                          {formatValue(value, header.dp)}
                        </td>
                      );
                    }
                    if (header.key === "target") {
                      const segments = lap.attackSegments;

                      // Build gradient from segments array using time fractions
                      let background;
                      if (lap.attackMode && segments && segments.length > 0) {
                        const gradientStops: string[] = [];
                        let cumulativePct = 0;

                        for (let i = 0; i < segments.length; i++) {
                          const startPct = cumulativePct * 100;
                          // Use time fractions if available, otherwise fall back to equal widths
                          const segmentFraction =
                            segmentTimeFractions?.[i] ?? 1 / segments.length;
                          cumulativePct += segmentFraction;
                          const endPct = cumulativePct * 100;
                          const color = segments[i] ? "#cd97e3" : "transparent";
                          gradientStops.push(`${color} ${startPct}%`);
                          gradientStops.push(`${color} ${endPct}%`);
                        }

                        background = `linear-gradient(to right, ${gradientStops.join(
                          ", ",
                        )})`;
                      }

                      // Calculate activation line position from segment time fractions
                      let activationLinePosition: number | null = null;
                      if (
                        lap.activationLap &&
                        emImport?.NSegmentEMAttackModeStarts != null &&
                        segmentTimeFractions
                      ) {
                        // Sum time fractions up to (but not including) the attack start segment
                        // NSegmentEMAttackModeStarts is 1-indexed
                        let cumulativeFraction = 0;
                        for (
                          let i = 0;
                          i < emImport?.NSegmentEMAttackModeStarts - 1;
                          i++
                        ) {
                          cumulativeFraction += segmentTimeFractions[i] ?? 0;
                        }
                        activationLinePosition = cumulativeFraction * 100;
                      }

                      return (
                        <td
                          key={header.key}
                          style={{
                            background,
                            position: "relative",
                          }}
                        >
                          {activationLinePosition != null && (
                            <div
                              style={{
                                position: "absolute",
                                left: `${activationLinePosition}%`,
                                top: 0,
                                bottom: 0,
                                width: "3px",
                                backgroundColor: "rgba(1, 1, 1, 0.3)",
                                zIndex: 0,
                              }}
                            />
                          )}
                          {lap.freezeLap && (
                            <div
                              style={{
                                position: "absolute",
                                left: 0,
                                right: 0,
                                bottom: 0,
                                height: "5px",
                                backgroundColor: "#339af0",
                                transform: "translateY(65%)",
                              }}
                            />
                          )}
                          <span style={{ position: "relative", zIndex: 1 }}>
                            {formatValue(lap.target, header.dp)}
                          </span>
                        </td>
                      );
                    }
                    if (header.key === "comment") {
                      return (
                        <td key={header.key}>
                          <input
                            key={`comment-input-${carColour}-${lapNum}`}
                            type="text"
                            className={styles.commentInput}
                            value={lap.comment ?? ""}
                            onChange={(e) => {
                              // Update locally for instant feedback
                              dispatch(
                                setLapCommentLocal({
                                  carColour,
                                  lapNumber: lapNum,
                                  comment: e.target.value,
                                }),
                              );
                              setLapComment({
                                variables: {
                                  carColour,
                                  lapNumber: lapNum,
                                  comment: e.target.value,
                                },
                              });
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.currentTarget.blur();
                              }
                            }}
                          />
                        </td>
                      );
                    }
                    if (header.key.startsWith("ch:")) {
                      const channelId = header.key.slice(3);
                      // Accept from either colour; the first in the list is the
                      // higher-priority (lower number) one for this channel.
                      let rawValue: string | number | null | undefined;
                      for (const colour of header.channelColours ?? []) {
                        const decodedChannels =
                          colour === "Orange"
                            ? lap.orangeCode?.decodedChannels
                            : lap.greenCode?.decodedChannels;
                        const candidate = decodedChannels?.[channelId];
                        if (candidate != null) {
                          rawValue = candidate;
                          break;
                        }
                      }
                      return (
                        <td key={header.key}>
                          {rawValue == null
                            ? "-"
                            : typeof rawValue === "number"
                            ? rawValue.toFixed(header.dp)
                            : rawValue}
                        </td>
                      );
                    }
                    return (
                      <td key={header.key}>
                        {formatValue(
                          lap[header.key as keyof typeof lap] as number | null,
                          header.dp,
                        )}
                      </td>
                    );
                  })}
                </tr>
                {lapNum === strategyImport?.NLapsEightyPercent && (
                  <tr>
                    <td
                      colSpan={visibleHeaders.length}
                      style={{
                        height: "3px",
                        padding: 0,
                        backgroundColor: "#ff6b6b",
                      }}
                    />
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
